import { useState } from 'react';
import { WrenchScrewdriverIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { useMcpConnectionTools } from '../../hooks/useMcpConnections';
import { McpToolInfo, McpConnectionErrorDetails, McpErrorPhase } from '../../types';

interface McpConnectionToolListProps {
  connectionId: string;
}

interface ToolCardProps {
  tool: McpToolInfo;
}

const ToolCard = ({ tool }: ToolCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasParameters = tool.parameters && Object.keys(tool.parameters).length > 0;

  const renderParameters = () => {
    if (!tool.parameters) return null;

    const params = tool.parameters as {
      type?: string;
      properties?: Record<string, { type?: string; description?: string }>;
      required?: string[];
    };

    if (params.type === 'object' && params.properties) {
      const required = params.required || [];
      return (
        <ul className="mt-2 space-y-1">
          {Object.entries(params.properties).map(([name, prop]) => (
            <li key={name} className="text-sm text-gray-600 dark:text-gray-300 flex items-start">
              <span className="text-gray-400 mr-2">•</span>
              <span>
                <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 py-0.5 rounded font-mono">
                  {name}
                </code>
                {prop.type && (
                  <span className="text-gray-500 dark:text-gray-400 ml-1">
                    ({prop.type}{required.includes(name) ? ', required' : ''})
                  </span>
                )}
                {prop.description && (
                  <span className="text-gray-500 dark:text-gray-400 ml-1">
                    - {prop.description}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      );
    }

    // Fallback: show raw JSON for complex schemas
    return (
      <pre className="mt-2 text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded overflow-x-auto">
        {JSON.stringify(tool.parameters, null, 2)}
      </pre>
    );
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-750 transition-colors text-left"
      >
        <div className="flex items-center space-x-2">
          <WrenchScrewdriverIcon className="h-4 w-4 text-gray-400" />
          <code className="text-sm font-medium text-gray-900 dark:text-white font-mono">
            {tool.name}
          </code>
        </div>
        {hasParameters && (
          isExpanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
          )
        )}
      </button>
      <div className="p-3 bg-white dark:bg-gray-800">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {tool.description || 'No description available'}
        </p>
        {hasParameters && isExpanded && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Parameters
            </h4>
            {renderParameters()}
          </div>
        )}
      </div>
    </div>
  );
};

export default function McpConnectionToolList({ connectionId }: McpConnectionToolListProps) {
  const [fetchEnabled, setFetchEnabled] = useState(false);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  const { data, isLoading, error, refetch, isFetching } = useMcpConnectionTools(
    connectionId,
    fetchEnabled
  );

  const handleFetch = () => {
    setFetchEnabled(true);
    setLastFetched(new Date());
  };

  const handleRefresh = () => {
    setLastFetched(new Date());
    refetch();
  };

  // Get troubleshooting tips based on error phase
  const getTroubleshootingTips = (phase: McpErrorPhase): string[] => {
    switch (phase) {
      case 'dns':
        return [
          'Check hostname spelling in the URL',
          'Verify the domain exists and is accessible',
          'Check DNS settings and network configuration',
        ];
      case 'connection':
        return [
          'Verify the MCP server is running',
          'Check that the port is not blocked by a firewall',
          'Confirm the URL is correct in connection settings',
        ];
      case 'tls':
        return [
          'Check certificate validity',
          'Verify TLS version compatibility',
          'Ensure the certificate chain is complete',
        ];
      case 'authentication':
        return [
          'Verify credentials are correct',
          'Check if token has expired',
          'Confirm the auth type matches server requirements',
        ];
      case 'timeout':
        return [
          'Server may be slow or overloaded',
          'Check network latency',
          'Try again later',
        ];
      case 'server':
        return [
          'Check MCP server logs',
          'Verify server configuration',
          'Try again later',
        ];
      case 'protocol':
        return [
          'Check MCP server compatibility',
          'Verify server is returning valid SSE responses',
          'Check server documentation',
        ];
      default:
        return [
          'Check connection configuration',
          'Verify server is accessible',
          'Check server logs for details',
        ];
    }
  };

  // Extract error details from the error response
  const getErrorDetails = (error: Error): McpConnectionErrorDetails | null => {
    // The error from React Query may have the details attached
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyError = error as any;
    if (anyError.details) {
      return anyError.details as McpConnectionErrorDetails;
    }
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <WrenchScrewdriverIcon className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">Available Tools</h2>
        </div>
        <div className="flex items-center space-x-3">
          {lastFetched && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Last fetched: {lastFetched.toLocaleTimeString()}
            </span>
          )}
          {!fetchEnabled ? (
            <button
              onClick={handleFetch}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Fetch Tools
            </button>
          ) : (
            <button
              onClick={handleRefresh}
              disabled={isFetching}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          )}
        </div>
      </div>

      {!fetchEnabled && !isLoading && (
        <div className="text-center py-8">
          <WrenchScrewdriverIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Click "Fetch Tools" to discover available tools from this MCP server
          </p>
        </div>
      )}

      {isLoading && (
        <div className="text-center py-8">
          <ArrowPathIcon className="mx-auto h-8 w-8 text-gray-400 animate-spin" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Connecting to MCP server...
          </p>
        </div>
      )}

      {error && (() => {
        const errorDetails = getErrorDetails(error as Error);
        const tips = errorDetails ? getTroubleshootingTips(errorDetails.phase) : [];

        return (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div className="ml-3 flex-1 min-w-0">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Failed to fetch tools
                  {errorDetails && (
                    <span className="ml-2 text-xs font-normal text-red-600 dark:text-red-400">
                      ({errorDetails.errorType})
                    </span>
                  )}
                </h3>

                {errorDetails ? (
                  <div className="mt-2 space-y-2">
                    {/* Target URL */}
                    <div>
                      <span className="text-xs font-medium text-red-700 dark:text-red-300">URL: </span>
                      <code className="text-xs text-red-600 dark:text-red-400 font-mono break-all">
                        {errorDetails.targetUrl}
                      </code>
                    </div>

                    {/* Headers (if any) */}
                    {Object.keys(errorDetails.headers).length > 0 && (
                      <div>
                        <span className="text-xs font-medium text-red-700 dark:text-red-300">Headers:</span>
                        <ul className="mt-1 space-y-0.5">
                          {Object.entries(errorDetails.headers).map(([key, value]) => (
                            <li key={key} className="text-xs text-red-600 dark:text-red-400 font-mono ml-2">
                              {key}: {value}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Original error */}
                    <div>
                      <span className="text-xs font-medium text-red-700 dark:text-red-300">Error: </span>
                      <span className="text-xs text-red-600 dark:text-red-400">
                        {errorDetails.originalError}
                      </span>
                    </div>

                    {/* Troubleshooting tips */}
                    {tips.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-800">
                        <span className="text-xs font-medium text-red-700 dark:text-red-300">Troubleshooting:</span>
                        <ul className="mt-1 space-y-0.5">
                          {tips.map((tip, index) => (
                            <li key={index} className="text-xs text-red-600 dark:text-red-400 ml-2">
                              • {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-red-700 dark:text-red-300">
                    {(error as Error).message || 'Failed to fetch tools from MCP server.'}
                  </p>
                )}

                <button
                  onClick={handleRefresh}
                  className="mt-3 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-500"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {data && !isLoading && !error && (
        <>
          {data.tools.length === 0 ? (
            <div className="text-center py-8">
              <WrenchScrewdriverIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                This MCP server has no tools available
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {data.tools.map((tool) => (
                  <ToolCard key={tool.name} tool={tool} />
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {data.toolCount} {data.toolCount === 1 ? 'tool' : 'tools'} available
                </p>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
